import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Medal, X, ArrowRight } from "lucide-react";

// One reusable founding-seat re-entry prompt. Counts are read LIVE from /founding/prompt on every
// mount (never cached, never hardcoded). It collapses to nothing the instant the member holds a
// seat, and to an honest "all claimed" line the instant public seats run out.
//   mode="card"    — Dashboard panel (dismissible; respects the 7-day/permanent dismiss)
//   mode="callout" — Profile & Badges banner (dismissible)
//   mode="inline"  — Account "Badges" line: permanent claim link (ignores dismiss, it's the fix
//                    for the dead-end line) + honest sold-out text
export default function FoundingClaimPrompt({ mode = "card", className = "" }) {
  const [p, setP] = useState(null);
  const [hidden, setHidden] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/founding/prompt").then(({ data }) => setP(data)).catch(() => setP(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismiss = () => { setHidden(true); api.post("/founding/prompt/dismiss").catch(() => {}); };

  if (!p || hidden || p.has_seat) return null; // reappears never after a claim

  // INLINE (Account → Badges) — fix the dead-end line. Permanent link while claimable.
  if (mode === "inline") {
    if (p.eligible) {
      return (
        <button onClick={() => navigate("/founding")} data-testid="founding-claim-inline-btn"
          className={`inline-flex items-center gap-2 border border-[#55451C] bg-[#14110A] hover:border-[#b08d2c] text-[#E8C46A] font-mono uppercase text-[11px] tracking-wide px-3 py-2 transition-colors ${className}`}>
          <Medal className="w-3.5 h-3.5" /> Claim your founding seat — {p.public_remaining} of {p.public_seats} open
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      );
    }
    if (p.sold_out) {
      return <p data-testid="founding-claim-soldout" className={`font-mono text-xs text-muted-foreground uppercase ${className}`}>All founding seats have been claimed.</p>;
    }
    return <p className={`font-mono text-xs text-muted-foreground uppercase ${className}`}>No founding seat on this account.</p>;
  }

  // CARD / CALLOUT — dismissible persistent nudge, only while claimable and not dismissed.
  if (!p.should_prompt) return null;

  return (
    <div data-testid={`founding-prompt-${mode}`}
      className={`bg-[#14110A] border border-[#55451C] px-[15px] py-[14px] flex items-start justify-between gap-4 ${className}`}>
      <div className="flex items-start gap-3 min-w-0">
        <Medal className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#E8C46A" }} />
        <div className="min-w-0">
          <p className="font-heading font-bold text-[16px] uppercase tracking-tight" style={{ color: "#E8C46A" }}>
            Claim your founding seat
          </p>
          <p className="text-[13px] font-body text-[#8B8D90] mt-0.5">
            Permanent and numbered — <span className="text-[#C8A64E]">{p.public_remaining} of {p.public_seats}</span> seats still open. Yours stays with you for good.
          </p>
          <button onClick={() => navigate("/founding")} data-testid="founding-prompt-claim-btn"
            className="mt-3 inline-flex items-center gap-2 bg-[#b08d2c] hover:brightness-110 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-all">
            Claim a seat <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <button onClick={dismiss} data-testid="founding-prompt-dismiss" title="Dismiss"
        className="shrink-0 text-[#76797D] hover:text-foreground transition-colors p-1"><X className="w-4 h-4" /></button>
    </div>
  );
}
